import type { VideoEditor } from './VideoEditor';

/**
 * Observer interface for the Editor
 * Any class that wants to observe editor changes should implement this interface
 */
export interface EditorObserver {
  // Method to be called when a change occurs
  onEditorChange(
    editor: VideoEditor,
    changeType: EditorChangeType,
    payload?: any,
  ): void;
}

export type ElementResizeResult = {
  mediaStart: number;
  mediaEnd: number;
  time: number;
  rippleAmount: number;
};

/**
 * Types of changes that can occur in the editor
 */
export enum EditorChangeType {
  // Timeline related
  TIMELINE_CHANGED = 'timeline_changed',
  PROJECT_LOADED = 'project_loaded',
  FRAMERATE_CHANGED = 'framerate_changed',
  SNAP_TO_FRAME_CHANGED = 'snap_to_frame_changed',

  // Transcript related
  TRANSCRIPT_LOADED = 'transcript_loaded',
  TRANSCRIPT_MODIFIED = 'transcript_modified',
  ACTIVE_TRANSCRIPT_CHANGED = 'active_transcript_changed',

  // Karaoke related
  KARAOKE_LOADED = 'karaoke_loaded',

  // Punch list related
  PUNCH_LIST_UPDATED = 'punch_list_updated',

  // Element related
  ELEMENT_ADDED = 'element_added',
  ELEMENT_REMOVED = 'element_removed',
  ELEMENT_UPDATED = 'element_updated',
  ELEMENT_SHIFTED = 'element_shifted',
  ELEMENT_TRACK_CHANGED = 'element_track_changed',
  ELEMENT_TRIMMED_START = 'element_trimmed_start',
  ELEMENT_TRIMMED_MEDIA_START = 'element_trimmed_media_start',
  ELEMENT_TRIMMED_END = 'element_trimmed_end',
  ELEMENT_MERGED = 'element_merged',
  ELEMENT_SPLIT = 'element_split',
  ELEMENT_SPLIT_UNDONE = 'element_split_undone',

  // Track related
  TRACK_ADDED = 'track_added',
  TRACK_REMOVED = 'track_removed',
  TRACK_LAYER_CHANGED = 'track_layer_changed',
  TRACKS_RENUMBERED = 'tracks_renumbered',

  // Scene related
  SCENE_ADDED = 'scene_added',
  SCENE_UPDATED = 'scene_updated',
  SCENE_REMOVED = 'scene_removed',

  // Batch related
  BATCH_STARTED = 'batch_started',
  BATCH_COMPLETED = 'batch_completed',

  // Command related
  COMMAND_EXECUTED = 'command_executed',
  COMMAND_UNDONE = 'command_undone',
  COMMAND_REDONE = 'command_redone',

  // Selection related
  SELECTION_CHANGED = 'selection_changed',

  // Integration related
  EXTERNAL_STATE_CHANGE = 'external_state_change',

  // External events
  FORCED_REFRESH = 'forced_refresh', // after accepting external changes

  // Undo/redo driven externally (snapshot-based stores restore state by
  // reloading the project rather than replaying the editor's own command
  // history, so observers that mirror element state must re-sync on these).
  EXTERNAL_UNDO = 'external_undo',
  EXTERNAL_REDO = 'external_redo',
}

export type TranscriptPunctToken = TranscriptTokenCommon & {
  type: 'punct';
};

export type TranscriptWhitespaceToken = TranscriptPunctToken & {
  subtype: 'whitespace';
  value: ' ';
};

export type TranscriptTextToken = TranscriptTokenCommon &
  TranscriptTextTokenTimelinePositionProps &
  Partial<TranscriptTextTokenMediaPositionProps> &
  TranscriptTextTokenCutPointProps & {
    type: 'text';
  };

type TranscriptTokenCommon = {
  id: string;
  // PLAT-10611 — original story-space element id, preserved across clip
  // id-regeneration so the translation join can match by UUID.
  source_uuid?: string;
  value: string | null;
  old_value?: string | undefined | null;
  state?: 'removed' | 'added' | 'replaced' | 'muted' | 'cut';
  initial_index: number;
} & TranscriptTokenMuteProps &
  TranscriptTokenKaraokeProps &
  TranscriptTokenSpeakerProps &
  TranscriptTokenHighlightProps;

type TranscriptTextTokenTimelinePositionProps = {
  ts: number | null;
  end_ts: number | null;
};

export type TimelinePositionPropsNotNull = {
  ts: number;
  end_ts: number;
};

type TranscriptTextTokenMediaPositionProps = {
  media_ts: number; // to keep manually adjusted timestamps relative to full video
  media_end_ts: number; // if not present, assumed to be as in original transcript
};

/** Where removing this word starts and ends, on the clock of `media_ts`. */
type TranscriptTextTokenCutPointProps = {
  media_cut_ts?: number;
  media_cut_end_ts?: number;
};

type TranscriptTokenMuteProps = {
  muted_by_hideFillers?: boolean;
  muted_by_hidePunct?: boolean;
};

type TranscriptTokenKaraokeProps = {
  karaoke_break?: boolean;
  karaoke_break_start_ts_diff?: number;
  karaoke_break_end_ts_diff?: number;
};

export type TranscriptTokenSpeakerProps = {
  segment_id?: string;
  paragraph_id?: string;
  speaker_label?: string;
  speaker_id?: string;
  original_speaker_label?: string;
};

// Mostly unused
type TranscriptTokenHighlightProps = {
  photo_highlight_id?: string;
  last_photo_highlight?: boolean;
  color?: string;
};

export type TranscriptTokenClipSegmentPositionProps = {
  // for simple lookup of a clip segment in legacy plain array transcript
  clip_segment_id: string;
  clip_segment_position: number;
  subtype?: 'whitespace';
};

export type TranscriptToken =
  | TranscriptTextToken
  | TranscriptPunctToken
  | TranscriptWhitespaceToken;

export type TranscriptTokenWithClipSegmentAttributes =
  TranscriptTokenClipSegmentPositionProps &
    (
      | (Omit<TranscriptTextToken, 'ts' | 'end_ts'> &
          TranscriptTextTokenMediaPositionProps &
          Partial<TranscriptTextTokenTimelinePositionProps>)
      | TranscriptPunctToken
      | TranscriptWhitespaceToken
    );

export type ActiveTranscriptTokenWithClipSegmentAttributes =
  TranscriptTokenClipSegmentPositionProps &
    (
      | (TranscriptTextToken & TranscriptTextTokenMediaPositionProps)
      | TranscriptPunctToken
      | TranscriptWhitespaceToken
    );

export type PunctuationMark = ' ' | ',' | '.' | '\n';

export type ClipTranscriptTextToken = TranscriptTokenCommon &
  TranscriptTextTokenMediaPositionProps &
  TranscriptTextTokenCutPointProps & {
    type: 'text';
    relative_ts: number; // relative to the media start
    relative_end_ts: number;
  };

export type ClipTranscriptToken =
  | ClipTranscriptTextToken
  | TranscriptPunctToken
  | TranscriptWhitespaceToken;
